import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';

import {
  CaretLeft,
  ChartBar,
  Check,
  Cookie,
  Megaphone,
  Shield,
  Sparkle,
  X
} from '@phosphor-icons/react';
const CookieSettings = () => {
  const navigate = useNavigate();
  const [lastUpdated, setLastUpdated] = useState(null);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    personalization: false
  });

  useEffect(() => {
    // Load current preferences
    const savedPreferences = localStorage.getItem('cookiePreferences');
    const consentDate = localStorage.getItem('cookieConsentDate');
    
    if (savedPreferences) {
      setPreferences(JSON.parse(savedPreferences));
    }
    
    if (consentDate) {
      setLastUpdated(consentDate);
    }
  }, []);

  const handleToggle = (category) => {
    if (category === 'essential') return; // Essential cannot be toggled
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    localStorage.setItem('cookieConsent', preferences.analytics || preferences.marketing || preferences.personalization ? 'partial' : 'essentialOnly');
    setLastUpdated(new Date().toISOString());
    
    toast.success('Cookie preferences saved successfully!');
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      personalization: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    localStorage.setItem('cookieConsent', 'accepted');
    setLastUpdated(new Date().toISOString());
    
    toast.success('All cookies accepted!');
  };

  const handleDeclineNonEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
      personalization: false
    };
    setPreferences(essentialOnly);
    localStorage.setItem('cookiePreferences', JSON.stringify(essentialOnly));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    localStorage.setItem('cookieConsent', 'essentialOnly');
    setLastUpdated(new Date().toISOString());
    
    toast.success('Non-essential cookies declined.');
  };

  const cookieCategories = [
    {
      key: 'essential',
      icon: Shield,
      title: 'Essential Cookies',
      description: 'Required for the website to function properly. These cannot be disabled.',
      examples: 'Authentication, security, session management, form submissions, shopping cart',
      color: 'green',
      alwaysActive: true
    },
    {
      key: 'analytics',
      icon: ChartBar,
      title: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website by collecting and reporting information anonymously.',
      examples: 'Google Analytics, page views, bounce rate, traffic sources',
      color: 'blue'
    },
    {
      key: 'marketing',
      icon: Megaphone,
      title: 'Marketing Cookies',
      description: 'Used to track visitors across websites to display ads that are relevant and engaging.',
      examples: 'Facebook Pixel, Google Ads, LinkedIn Insight Tag, retargeting campaigns',
      color: 'orange'
    },
    {
      key: 'personalization',
      icon: Sparkle,
      title: 'Personalization Cookies',
      description: 'Remember your preferences and choices to provide a customized experience.',
      examples: 'Language preferences, theme settings, content recommendations, saved filters',
      color: 'purple'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      green: 'bg-green-500/10 border-green-500/20',
      blue: 'bg-blue-500/10 border-blue-500/20',
      orange: 'bg-orange-500/10 border-orange-500/20',
      purple: 'bg-purple-500/10 border-purple-500/20'
    };
    return colors[color] || colors.purple;
  };

  const springConfig = {
    type: 'spring',
    damping: 80,
    stiffness: 400,
    mass: 1,
  };

  return (
    <div className="bg-[#161B26]">
      <SEO 
        title="Cookie Settings - Man & Machine"
        description="Manage your cookie preferences and control how we use cookies on our website."
        canonicalUrl="https://manmachine.agency/cookie-settings"
      />
      <Header />
      

      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#6366F1] rounded-full filter blur-3xl opacity-20"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="max-w-4xl mx-auto"
          >
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-[#6366F1] hover:text-[#8B5CF6] transition-colors mb-6"
            >
              <CaretLeft size={20} />
              <span className="text-sm font-medium">Back</span>
            </button>

            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-full blur-md opacity-50"></div>
                <div className="relative p-3 rounded-full bg-gradient-to-br from-[#6366F1] to-[#8B5CF6]">
                  <Cookie size={32} className="text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-4xl font-bold text-white">Cookie Settings</h1>
                <p className="text-[#A0AEC0] mt-1">Manage your cookie preferences and privacy settings</p>
              </div>
            </div>

            {lastUpdated && (
              <p className="text-sm text-[#718096] bg-[rgba(99,102,241,0.1)] px-4 py-2 rounded-lg inline-block">
                Last Updated: {new Date(lastUpdated).toLocaleString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Cookie Categories */}
      <section className="pb-20 bg-[#161B26]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.1 }}
            className="space-y-4"
          >
            {cookieCategories.map((category, index) => {
              const Icon = category.icon;
              const isEnabled = preferences[category.key];

              return (
                <motion.div
                  key={category.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...springConfig, delay: 0.1 * (index + 1) }}
                  className={`glass rounded-2xl p-6 border ${getColorClasses(category.color)}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-2.5 rounded-lg bg-[rgba(255,255,255,0.05)]">
                        <Icon size={24} className="text-white" />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-2">
                          {category.title}
                        </h3>
                        <p className="text-[#A0AEC0] text-sm leading-relaxed mb-3">
                          {category.description}
                        </p>
                        <p className="text-xs text-[#718096]">
                          <span className="font-medium">Examples:</span> {category.examples}
                        </p>
                      </div>
                    </div>

                    {/* Toggle or Always Active Badge */}
                    <div className="flex-shrink-0">
                      {category.alwaysActive ? (
                        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30">
                          <Check size={16} className="text-green-400" />
                          <span className="text-sm font-medium text-green-400">Always Active</span>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleToggle(category.key)}
                          className={`relative w-14 h-7 rounded-full transition-colors ${
                            isEnabled ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6]' : 'bg-[rgba(255,255,255,0.1)]'
                          }`}
                        >
                          <motion.div
                            className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-lg"
                            animate={{ left: isEnabled ? '30px' : '4px' }}
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                          />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.5 }}
            className="mt-8 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={handleDeclineNonEssential}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-[rgba(255,255,255,0.2)] text-white hover:bg-[rgba(255,255,255,0.05)] transition-all font-medium"
            >
              <X size={18} />
              Decline Non-Essential
            </button>
            
            <button
              onClick={handleSavePreferences}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[rgba(99,102,241,0.2)] border border-[#6366F1]/30 text-white hover:bg-[rgba(99,102,241,0.3)] transition-all font-medium"
            >
              <Check size={18} />
              Save Preferences
            </button>
            
            <button
              onClick={handleAcceptAll}
              className="relative overflow-hidden rounded-lg group px-6 py-3 flex-1"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] via-[#8B5CF6] to-[#EC4899]"></div>
              <span className="relative z-10 text-white font-medium">
                Accept All Cookies
              </span>
            </button>
          </motion.div>

          {/* Info Note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8 p-4 rounded-lg bg-[rgba(99,102,241,0.1)] border border-[#6366F1]/20"
          >
            <p className="text-sm text-[#A0AEC0] leading-relaxed">
              <strong className="text-white">Note:</strong> Your choices will be saved and remembered for 6 months. 
              You can change your preferences at any time by returning to this page.
              For more information about how we use cookies, please read our{' '}
              <a href="/privacy" className="text-[#6366F1] hover:text-[#8B5CF6] underline transition-colors">
                Privacy Policy
              </a>.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CookieSettings;
